import { useLazyQuery } from '@apollo/client';
import i18n from '@assets/i18n/config';
import type { FinancialProduct } from '@interfaces/financialProduct';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useState } from 'react';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  assignMissingTaxRanks,
  buildSecurityMap,
  deduplicateTaxRanks,
  extractUniqueTickers,
  formatAvailableSecurities,
  formatImportErrors,
  parseCsvFile,
  processValidatedRows,
  type UnrecognizedSecurity,
  validateFileSize,
  validateParsedCsv,
  validateTaxRanks,
} from '../addModelPortfolioSecurities/helpers/csvImportHelpers';
import { FETCH_SECURITIES } from '../addModelPortfolioSecurities/queries';

const CSV_MAX_ROWS = 100;
const CSV_MAX_FILE_SIZE_MB = 5;
const CSV_MIN_ROWS = 1;
const BYTES_PER_MB = 1024 * 1024;
const FULL_PERCENTAGE = 100;
const PERCENTAGE_TOLERANCE = 0.01;
const SECURITIES_PER_PAGE = 1000;
const SECURITIES_SORT_FIELD = 'ticker';
const SECURITIES_STATE = 'ACTIVE';
const SECURITY_IDENTIFIER_COLUMN = 'securityIdentifier(Ticker)';

export type PendingImportData = {
  rows: Record<string, string>[];
  securityMap: Map<string, FinancialProduct>;
  availableSecurities: Array<{ id: string; ticker: string; name: string }>;
};

type UseCsvSecuritiesImportOptions = {
  existingSecurities: Security[];
  onImportComplete: (securities: Security[]) => void;
  /**
   * When true (Asset Class / grouped model), each asset group's securities are
   * expected to sum to 100% independently, so the flat overall total can exceed
   * 100%. The per-security validation (0–100 per row) still applies; only the
   * aggregate-sum check is skipped.
   */
  isAssetClassStrategy?: boolean;
};

type UseCsvSecuritiesImportReturn = {
  importLoading: boolean;
  showReviewDialog: boolean;
  unrecognizedSecurities: UnrecognizedSecurity[];
  pendingImportData: PendingImportData | null;
  handleImportFromCsv: (
    file: File,
    onProgress?: (progress: number, message?: string) => void,
  ) => Promise<void>;
  handleReviewSubmit: (replacements: Record<string, string>) => Promise<void>;
  handleReviewClose: () => void;
};

const buildFetchInput = (
  organizationId: string | undefined,
  tickers?: string[],
) => ({
  input: {
    filter: {
      organizationId,
      state: SECURITIES_STATE,
      ...(tickers ? { tickers } : {}),
    },
    pagination: {
      perPage: SECURITIES_PER_PAGE,
      sortField: SECURITIES_SORT_FIELD,
    },
  },
});

const mergeAndFinalizeSecurities = (
  newSecurities: Security[],
  existingSecurities: Security[],
): Security[] => {
  const importedIds = new Set(newSecurities.map((s) => s.financialProduct.id));
  const existingNotInCsv = existingSecurities.filter(
    (s) => !importedIds.has(s.financialProduct.id),
  );
  return deduplicateTaxRanks([...newSecurities, ...existingNotInCsv]);
};

export const useCsvSecuritiesImport = ({
  existingSecurities,
  onImportComplete,
  isAssetClassStrategy = false,
}: UseCsvSecuritiesImportOptions): UseCsvSecuritiesImportReturn => {
  const { activeOrganization } = useContext(UserContext);
  const { language } = useLocalization();
  const [fetchSecurities] = useLazyQuery(FETCH_SECURITIES);

  const [importLoading, setImportLoading] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [unrecognizedSecurities, setUnrecognizedSecurities] = useState<
    UnrecognizedSecurity[]
  >([]);
  const [pendingImportData, setPendingImportData] =
    useState<PendingImportData | null>(null);

  const handleReviewClose = () => {
    setShowReviewDialog(false);
    setUnrecognizedSecurities([]);
    setPendingImportData(null);
  };

  const handleImportFromCsv = async (
    file: File,
    onProgress?: (progress: number, message?: string) => void,
  ) => {
    if (importLoading) return;
    setImportLoading(true);

    try {
      onProgress?.(5, i18n.t('modelPortfolios:importCsv.progress.parsing'));
      validateFileSize(file, CSV_MAX_FILE_SIZE_MB);

      const rows = await parseCsvFile(file, { maxRows: CSV_MAX_ROWS });

      onProgress?.(
        15,
        i18n.t('modelPortfolios:importCsv.progress.validatingData'),
      );
      validateParsedCsv(file, rows, {
        maxRows: CSV_MAX_ROWS,
        minRows: CSV_MIN_ROWS,
        uniqueColumn: SECURITY_IDENTIFIER_COLUMN,
        maxFileSizeBytes: CSV_MAX_FILE_SIZE_MB * BYTES_PER_MB,
      });

      if (!isAssetClassStrategy) {
        const csvRowTotal = rows.reduce(
          (sum, row) =>
            sum + Number.parseFloat(row.targetWeight?.trim() || '0'),
          0,
        );
        if (Math.abs(csvRowTotal - FULL_PERCENTAGE) > PERCENTAGE_TOLERANCE) {
          const errorMessage = i18n.t(
            'modelPortfolios:importCsv.errors.totalAllocation',
            {
              total: FULL_PERCENTAGE,
              current: csvRowTotal.toFixed(2),
            },
          );
          throw new Error(errorMessage);
        }
      }

      onProgress?.(
        25,
        i18n.t('modelPortfolios:importCsv.progress.extractingSecurities'),
      );
      const uniqueTickers = extractUniqueTickers(rows);

      onProgress?.(
        40,
        i18n.t('modelPortfolios:importCsv.progress.fetchingSecurities'),
      );
      const matchedResponse = await fetchSecurities({
        variables: buildFetchInput(
          activeOrganization.id,
          Array.from(uniqueTickers),
        ),
      });

      const matchedSecurities: FinancialProduct[] =
        matchedResponse?.data?.fetchFinancialProducts?.financialProducts ?? [];

      onProgress?.(
        60,
        i18n.t('modelPortfolios:importCsv.progress.processingSecuritiesData'),
      );
      const securityMap = buildSecurityMap(matchedSecurities);

      onProgress?.(
        75,
        i18n.t('modelPortfolios:importCsv.progress.validatingRows'),
      );
      const {
        securities: newSecurities,
        errors,
        missingSecurities,
        hasErrors,
      } = processValidatedRows(rows, securityMap);

      if (hasErrors) throw new Error(formatImportErrors(errors));

      if (missingSecurities.length > 0) {
        onProgress?.(
          80,
          i18n.t(
            'modelPortfolios:importCsv.progress.fetchingReplacementSecurities',
          ),
        );
        const allResponse = await fetchSecurities({
          variables: buildFetchInput(activeOrganization.id),
        });

        const allSecurities: FinancialProduct[] =
          allResponse?.data?.fetchFinancialProducts?.financialProducts ?? [];

        setUnrecognizedSecurities(missingSecurities);
        setPendingImportData({
          rows,
          securityMap,
          availableSecurities: formatAvailableSecurities(
            allSecurities,
            language,
          ),
        });
        setShowReviewDialog(true);
        return;
      }

      onProgress?.(95, i18n.t('modelPortfolios:importCsv.progress.finalizing'));
      onImportComplete(
        mergeAndFinalizeSecurities(newSecurities, existingSecurities),
      );
      onProgress?.(100, i18n.t('modelPortfolios:importCsv.progress.complete'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleReviewSubmit = async (replacements: Record<string, string>) => {
    if (!pendingImportData) return;

    const { rows, securityMap } = pendingImportData;

    const missingReplacements = unrecognizedSecurities.filter(
      (sec) => !replacements[sec.ticker] || replacements[sec.ticker] === '',
    );
    if (missingReplacements.length > 0) {
      const errorMessage = i18n.t(
        'modelPortfolios:importCsv.errors.selectReplacements',
        {
          tickers: missingReplacements.map((s) => s.ticker).join(', '),
        },
      );
      throw new Error(errorMessage);
    }

    const allResponse = await fetchSecurities({
      variables: buildFetchInput(activeOrganization.id),
    });
    const allAvailable: FinancialProduct[] =
      allResponse?.data?.fetchFinancialProducts?.financialProducts ?? [];
    const allSecuritiesMap = new Map<string, FinancialProduct>(
      allAvailable.filter((p) => p.id).map((p) => [p.id!, p]),
    );

    const securityAggregation = new Map<
      string,
      { security: FinancialProduct; totalWeight: number; taxRank: number }
    >();
    const providedTaxRanks: number[] = [];

    rows.forEach((row) => {
      const identifier = row[SECURITY_IDENTIFIER_COLUMN]?.trim();
      if (!identifier) return;

      const targetWeight = Number.parseFloat(row.targetWeight?.trim() || '0');
      const taxRankStr = row.taxRank?.trim();
      const identifierKey = identifier.toUpperCase();

      if (taxRankStr) {
        const taxRank = Number.parseInt(taxRankStr, 10);
        if (!Number.isNaN(taxRank) && taxRank >= 1)
          providedTaxRanks.push(taxRank);
      }

      const matchedSecurity =
        securityMap.get(identifierKey) ??
        allSecuritiesMap.get(
          replacements[identifier] ?? replacements[identifierKey] ?? '',
        );
      if (!matchedSecurity) return;

      const securityId = matchedSecurity.id ?? '';
      const parsedTaxRank = taxRankStr ? Number.parseInt(taxRankStr, 10) : 0;
      const validTaxRank =
        !Number.isNaN(parsedTaxRank) && parsedTaxRank >= 1 ? parsedTaxRank : 0;
      const existing = securityAggregation.get(securityId);

      if (existing) {
        existing.totalWeight += targetWeight;
        if (validTaxRank > 0) {
          existing.taxRank =
            existing.taxRank === 0
              ? validTaxRank
              : Math.min(existing.taxRank, validTaxRank);
        }
      } else {
        securityAggregation.set(securityId, {
          security: matchedSecurity,
          totalWeight: targetWeight,
          taxRank: validTaxRank,
        });
      }
    });

    const taxRankError = validateTaxRanks(providedTaxRanks);
    if (taxRankError) throw new Error(taxRankError);

    const newSecurities: Security[] = [];
    securityAggregation.forEach((agg) => {
      newSecurities.push({
        id: agg.security.id,
        financialProduct: agg.security,
        percentage: agg.totalWeight,
        taxRank: agg.taxRank,
        isCashEquivalent: false,
      });
    });

    assignMissingTaxRanks(newSecurities, providedTaxRanks);

    onImportComplete(
      deduplicateTaxRanks([
        ...newSecurities,
        ...existingSecurities.filter(
          (s) =>
            !new Set(newSecurities.map((n) => n.financialProduct.id)).has(
              s.financialProduct.id,
            ),
        ),
      ]),
    );
    handleReviewClose();
  };

  return {
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    pendingImportData,
    handleImportFromCsv,
    handleReviewSubmit,
    handleReviewClose,
  };
};
